// ================= CHALLENGE ROTATION PACK 01 =================
//
// Twelve AI-only cards, three per themed rotation set: Midnight Metro, Harvest Havoc, Winter Fight
// Camp, Carnival After Dark. deploy_handoff/challenge-rotation-plan.md is the integration plan; the
// designer's package is the authority for what these cards DO, and its card data is vendored at
// `deploy_handoff/seasons/challenge_rotation_01_cards.v1.json` -- the pack itself is 32 MB of
// mostly-unused art and is not committed, exactly as Season 1's was not.
//
// NOBODY OWNS THESE, EVER, AT ANY PRICE. All twelve are `ownershipTier: 'Challenge'` — the tier
// js/cards-season-summer.js introduced for the Beach Ball and the Metal Detector. It is already
// excluded from `DeckRules.legalPool` (exclusion is the default for an unrecognised tier), reported
// by `validate` as its own CHALLENGE_ONLY issue, framed in `--challenge` gold by
// `CardTile.ownershipColor`, and absent from the store's catalog. The package marks them
// `futureRelease.eligible` with `requiresBalanceRevalidation`, which is PLANNING METADATA and is
// deliberately not transcribed: a card becomes ownable through a versioned content change and a
// price in lib/store/catalog.js, never through a flag on the card. Nothing here is a promise.
//
// TRANSCRIBED, NOT INGESTED — the same deliberate deviation from the package's
// INTEGRATION_CONTRACT.md that Season 1 recorded, and for the same reason. That contract asks the
// engine to "dispatch on `effect.type` and `condition.kind`" out of its JSON at runtime; this
// engine has never worked that way. A card is authored JS whose hooks close over a ctx of named
// helpers, and its taxonomy rides in a `v4` block. Building a second, data-driven effect runtime
// beside the one 211 cards already use would mean two ways for a card to do the same thing and two
// places for a rules bug to hide. So the designer's structured effects are the SPECIFICATION each
// hook is written against, and the specification is quoted beside every one of them.
//
// EVERY EFFECT HERE ALREADY HAD A TRANSLATION. The package's whole vocabulary is a strict subset of
// the one Season 1 landed — `modifyPower`, `ignoreGuard`, `createOpening`, `setOffBalance`,
// `shiftFlow`; conditions `pressureMovedClashCloser`, `evadeMovedClashFarther`, `cleanHit`,
// `attackDealsDamage`, `fullGuard`, `opponentDefenseCoverageCountAtLeast`; continuations `Chain`,
// `Control`, `ImpactRecovery`. Each hook below names the shipped card it copies its idiom from, so
// none of this is a fresh judgement about what the designer meant.
//
// TWO SIGN CONVENTIONS THAT ARE EASY TO INVERT, both settled by reading js/engine.js rather than by
// guessing. `ctx.shiftFlow(key, n)` computes `furyFlow - n` and `ctx.shiftFury(key, n)` computes
// `furyFlow + n`, on a single signed track where negative is Flow. So the designer's `shiftFlow`
// with `amount: -1` is `ctx.shiftFlow(x, 1)` (one step toward Flow) and the same effect with
// `amount: +1` is `ctx.shiftFury(x, 1)`. Both appear below and they are NOT the same call.
//
// WHAT THE DESIGNER DID NOT SUPPLY and is authored here: `attackBeat`/`defenseBeat` (the narrative
// layer needs a prose line per half), `defenseMethod`, the printed `text` in house grammar, and the
// `v4` badge/summary strings. Written to each set's own register — the taglines set it, and none of
// them is the summer comedy: the Metro is terse, the Harvest is rural and unhurried, the Camp is
// clipped and cold, the Carnival is theatrical.
//
// LOADED AFTER js/cards-v4.js, which matters for the same reason the summer file records: that file
// attaches `v4` by looping over its own 194 ids and would not touch these, but the `v4` blocks below
// are authored inline and must not be overwritten by a later pass.
(function () {
  'use strict';

  // Stamped on every card this pack adds, and READ BY THE TESTS rather than by the game. Several
  // suites pin the archive at exactly 194 cards — the Speed Edition is a fixed historical artefact
  // and those comparisons are meant to keep running against exactly it. A pack that grew the pool
  // without a way to filter itself back out would break them all; `seasonal` is that way, and this
  // is the second value it has taken.
  const SEASON = 'challenge-rotation-01';

  Object.assign(window.CARDS, {

    // =============================================================================================
    // MIDNIGHT METRO — "Last train. No easy exits."
    // =============================================================================================

    // ---------------------------------------------------------------------------------------------
    // CRP-MM-01 — Express Line
    //
    // Designer: +1 Power when `pressureMovedClashCloser` — you chose Pressure AND the clash landed
    // closer than it started. Both halves, which is why the hook reads the Intent as well as the
    // movement: a clash that drifted closer for someone else's reasons is not this card's payoff.
    // Transcribed from sfs_long_guard_spear_elbow, which carries the identical clause.
    // ---------------------------------------------------------------------------------------------
    mm_express_line: {
      id: 'mm_express_line', name: 'Express Line', style: 'Universal', tempo: 32,
      ownershipTier: 'Challenge', ownershipGroup: 'ChallengeOnly',
      seasonal: SEASON, displayId: 'CRP-MM-01',
      traits: ['Kick', 'Linear', 'Pressure'],
      moveGloss: 'Drive straight through the opening before the line can close.',
      attackBeat: 'takes the express route and drives a straight kick down the centre',
      defenseBeat: 'slips sideways and lets the attack thunder past',
      defenseMethod: 'Backstep',
      attack: {
        optimalRanges: ['Long', 'Pocket'], levels: ['Mid'], power: 3, cost: 0, category: 'Kick',
        // Designer: modifyPower +1, beforeGuard, condition `pressureMovedClashCloser`.
        powerMod: (ctx) => (ctx.attChoice.positionIntent === 'Pressure' && ctx.clashCloser) ? 1 : 0,
        text: 'Opt L/P • M • P3. If you chose Pressure and Clash is closer than Start, +1 Power.'
      },
      // Designer: Dodge, guard 2, coverage Mid/Low, end { shift, farther, 1 }. The end shift is
      // engine-driven off `defenseResult.end` (dodgeEndToEffects), so it needs no hook — the same
      // arrangement every shipped Dodge uses.
      defense: { levels: ['Mid', 'Low'], guard: 2, text: 'M/L • G2.' },
      v4: {
        technique: 'Express Line', family: 'Kick', speedPips: 2, speedLabel: 'Standard',
        continuation: { kind: 'ImpactRecovery', impactOnAbsorb: 1, recoveryOpeningLevel: 'Mid',
                        recoveryOpeningType: 'Recovery', recoveryOpeningBonus: 1,
                        recoveryTrigger: ['Deflect', 'Dodge'] },
        defenseFamily: 'Dodge',
        defenseResult: { contact: false, attackBecomesWhiff: true,
                         end: { kind: 'shift', direction: 'farther', bands: 1 } },
        attackBadge: 'KICK - IMPACT 1 through Absorb; RECOVERY -> MID after Deflect or Dodge.',
        defenseBadge: 'DODGE - Attack Whiffs; move End Range 1 band farther.',
        atkSummary: 'Range L/P | Level M | Power P3 | Kick',
        defSummary: 'Level M/L | Guard 2'
      }
    },

    // ---------------------------------------------------------------------------------------------
    // CRP-MM-02 — Closing Doors
    //
    // Both halves hold the range where it is, which is the card's whole idea: the doors shut and the
    // exchange stays where it stands. `holdClash` on the Attack's Control is the shipped idiom
    // (Clinch Dump and friends); `holdClash` on a DENY is rarer and is the designer's explicit
    // choice here — the package writes `control: { kind: holdClash }` on this defense and writes no
    // control at all on Scarecrow Frame's, so the two are deliberately different cards.
    // ---------------------------------------------------------------------------------------------
    mm_closing_doors: {
      id: 'mm_closing_doors', name: 'Closing Doors', style: 'Universal', tempo: 22,
      ownershipTier: 'Challenge', ownershipGroup: 'ChallengeOnly',
      seasonal: SEASON, displayId: 'CRP-MM-02',
      traits: ['Grapple', 'Clinch', 'Control'],
      moveGloss: 'Clamp both arms inside and leave no lane to escape.',
      attackBeat: 'clamps both arms inside and shuts the escape lane',
      defenseBeat: 'seals the centre and refuses to give up an inch',
      defenseMethod: 'Underhook',
      attack: {
        optimalRanges: ['Pocket', 'Clinch'], levels: ['Mid'], power: 2, cost: 0, category: 'Grapple',
        text: 'Opt P/C • M • P2.'
      },
      defense: { levels: ['High', 'Mid'], guard: 3, text: 'H/M • G3.' },
      v4: {
        technique: 'Closing Doors', family: 'Grapple', speedPips: 3, speedLabel: 'Fast',
        continuation: { kind: 'Control', contactResult: 'holdClash', trigger: 'contact' },
        defenseFamily: 'Deny',
        defenseResult: { contact: false, cancelContinuation: true, control: 'holdClash' },
        attackBadge: 'GRAPPLE - CONTROL on Contact: hold End at Clash.',
        defenseBadge: 'DENY - Cancel continuation; hold End at Clash.',
        atkSummary: 'Range P/C | Level M | Power P2 | Grapple',
        defSummary: 'Level H/M | Guard 3'
      }
    },

    // ---------------------------------------------------------------------------------------------
    // CRP-MM-03 — Mind the Gap
    //
    // Designer: setOffBalance, afterDamage, opponent, duration nextExchange, condition `cleanHit`.
    // Transcribed from sfs_improvised_detector_sweep, whose comment records both halves of the
    // translation: `cleanHitEffectsAllowed` rather than bare `clean` (a clean hit at Compromised
    // range is not a clean hit for the purpose of triggering an effect, which is the gate every
    // comparable shipped card uses), and `duration: nextExchange` needing nothing added because that
    // is already exactly how long Off-Balance lasts in this engine.
    // ---------------------------------------------------------------------------------------------
    mm_mind_the_gap: {
      id: 'mm_mind_the_gap', name: 'Mind the Gap', style: 'Universal', tempo: 34,
      ownershipTier: 'Challenge', ownershipGroup: 'ChallengeOnly',
      seasonal: SEASON, displayId: 'CRP-MM-03',
      traits: ['Grapple', 'Trip', 'LowLine'],
      moveGloss: 'Hook the near ankle and turn empty space into a trap.',
      attackBeat: 'hooks the near ankle and turns the empty space into a trap',
      defenseBeat: 'redirects the low entry before the footing can be stolen',
      defenseMethod: 'Sprawl',
      attack: {
        optimalRanges: ['Pocket', 'Clinch'], levels: ['Low'], power: 3, cost: 0, category: 'Grapple',
        onResolved: (ctx, result) => {
          if (result.cleanHitEffectsAllowed) ctx.setOffBalance(ctx.opp);
        },
        text: 'Opt P/C • L • P3. On a clean hit, the opponent is Off-Balance.'
      },
      defense: { levels: ['Mid', 'Low'], guard: 3, text: 'M/L • G3.' },
      v4: {
        technique: 'Mind the Gap', family: 'Grapple', speedPips: 2, speedLabel: 'Standard',
        continuation: { kind: 'Control', contactResult: 'setPocket', trigger: 'contact' },
        defenseFamily: 'Deflect',
        defenseResult: { contact: false, cancelContinuation: true, openingOn: 'attacker',
                         openingLevel: 'attemptedLevelUnlessRecoveryPrinted', bonus: 1,
                         againstGrappleControl: 'holdClash' },
        attackBadge: 'GRAPPLE - CONTROL on Contact: set End at Pocket.',
        defenseBadge: 'DEFLECT - Cancel continuation; create an Opening on the attacker at the resolved exposure level.',
        atkSummary: 'Range P/C | Level L | Power P3 | Grapple',
        defSummary: 'Level M/L | Guard 3'
      }
    },

    // =============================================================================================
    // HARVEST HAVOC — "Every opening comes home at harvest."
    // =============================================================================================

    // ---------------------------------------------------------------------------------------------
    // CRP-HH-01 — Scarecrow Frame
    //
    // A Power 1 attack face on a Guard 4 defense: the card is a wall that sets up the next punch.
    //
    // THE FIRST DENY IN THE GAME WITH NO END EFFECT, and that is transcription rather than an
    // omission. All 75 Deny cards in the archive carry a `control`; the package gives this one none
    // while giving Closing Doors an explicit `holdClash`, so the difference is the designer's. The
    // engine already handles it — `controlToEndEffects` falls through to `default: return []` — and
    // what it means is that this Deny buys the cancellation and nothing positional, which is what
    // makes Guard 4 affordable on it.
    // ---------------------------------------------------------------------------------------------
    hh_scarecrow_frame: {
      id: 'hh_scarecrow_frame', name: 'Scarecrow Frame', style: 'Universal', tempo: 20,
      ownershipTier: 'Challenge', ownershipGroup: 'ChallengeOnly',
      seasonal: SEASON, displayId: 'CRP-HH-01',
      traits: ['Punch', 'Frame', 'Read'],
      moveGloss: 'Spread a long frame across the shoulders and make the lane look wider than it is.',
      attackBeat: 'spreads a long frame across the shoulders and points the next punch through it',
      defenseBeat: 'spreads both arms wide and swallows every upper-line lane',
      defenseMethod: 'Frame',
      attack: {
        optimalRanges: ['Long', 'Pocket'], levels: ['High'], power: 1, cost: 0, category: 'Punch',
        text: 'Opt L/P • H • P1.'
      },
      defense: { levels: ['High', 'Mid'], guard: 4, text: 'H/M • G4.' },
      v4: {
        technique: 'Scarecrow Frame', family: 'Punch', speedPips: 3, speedLabel: 'Fast',
        continuation: { kind: 'Chain', openingLevel: 'Mid', openingType: 'Combination', bonus: 1,
                        usableBy: ['Punch'], available: 'nextExchangeOnly', trigger: 'contact' },
        defenseFamily: 'Deny',
        defenseResult: { contact: false, cancelContinuation: true },
        attackBadge: 'PUNCH - CHAIN -> MID: Contact creates the printed Punch-only Opening for next exchange.',
        defenseBadge: 'DENY - Cancel continuation.',
        atkSummary: 'Range L/P | Level H | Power P1 | Punch',
        defSummary: 'Level H/M | Guard 4'
      }
    },

    // ---------------------------------------------------------------------------------------------
    // CRP-HH-02 — Corn Maze Cutback
    //
    // Designer: +1 Power when `evadeMovedClashFarther` — the mirror of Express Line's clause on the
    // other Intent, and transcribed from sfs_step_behind_hook_kick, which carries it verbatim.
    // Both halves again: the attacker's own Evade AND the movement it produced.
    // ---------------------------------------------------------------------------------------------
    hh_corn_maze_cutback: {
      id: 'hh_corn_maze_cutback', name: 'Corn Maze Cutback', style: 'Universal', tempo: 22,
      ownershipTier: 'Challenge', ownershipGroup: 'ChallengeOnly',
      seasonal: SEASON, displayId: 'CRP-HH-02',
      traits: ['Kick', 'Footwork', 'Angle'],
      moveGloss: 'Vanish around the corner and whip the kick back through the new lane.',
      attackBeat: 'disappears around one angle and whips the kick back from another',
      defenseBeat: 'cuts around the corner and leaves the attack chasing nobody',
      defenseMethod: 'AngleStep',
      attack: {
        optimalRanges: ['Long', 'Pocket'], levels: ['Mid'], power: 2, cost: 0, category: 'Kick',
        // Designer: modifyPower +1, beforeGuard, condition `evadeMovedClashFarther`.
        powerMod: (ctx) => (ctx.attChoice.positionIntent === 'Evade' && ctx.clashFarther) ? 1 : 0,
        text: 'Opt L/P • M • P2. If you chose Evade and Clash is farther than Start, +1 Power.'
      },
      defense: { levels: ['High', 'Low'], guard: 3, text: 'H/L • G3.' },
      v4: {
        technique: 'Corn Maze Cutback', family: 'Kick', speedPips: 3, speedLabel: 'Fast',
        continuation: { kind: 'ImpactRecovery', impactOnAbsorb: 1, recoveryOpeningLevel: 'Mid',
                        recoveryOpeningType: 'Recovery', recoveryOpeningBonus: 1,
                        recoveryTrigger: ['Deflect', 'Dodge'] },
        defenseFamily: 'Dodge',
        defenseResult: { contact: false, attackBecomesWhiff: true,
                         end: { kind: 'shift', direction: 'farther', bands: 1 } },
        attackBadge: 'KICK - IMPACT 1 through Absorb; RECOVERY -> MID after Deflect or Dodge.',
        defenseBadge: 'DODGE - Attack Whiffs; move End Range 1 band farther.',
        atkSummary: 'Range L/P | Level M | Power P2 | Kick',
        defSummary: 'Level H/L | Guard 3'
      }
    },

    // ---------------------------------------------------------------------------------------------
    // CRP-HH-03 — Pumpkin Roll
    //
    // Designer: requires `opponentHasOpening` minimum 1, costs 1 Stamina at commit. The requirement
    // idiom takes (fighter, opponent) and gates the card at SELECTION rather than at resolution —
    // the same shape hip_throw's `opp.offBalance` uses, and the one sfs_uki_otoshi already
    // copied for this exact condition. The heaviest card in the pack and the only Committed Grapple.
    // ---------------------------------------------------------------------------------------------
    hh_pumpkin_roll: {
      id: 'hh_pumpkin_roll', name: 'Pumpkin Roll', style: 'Universal', tempo: 48,
      ownershipTier: 'Challenge', ownershipGroup: 'ChallengeOnly',
      seasonal: SEASON, displayId: 'CRP-HH-03',
      traits: ['Grapple', 'Roll', 'LowLine', 'Kuzushi'],
      moveGloss: 'Drop beneath the broken stance and roll the hips through the low line.',
      attackBeat: 'drops beneath the broken stance and rolls the hips clean through the low line',
      defenseBeat: 'sinks into a broad base and absorbs the rolling entry',
      defenseMethod: 'HipPressure',
      requirement: (f, opp) => (opp.openings || []).length >= 1,
      requirementText: 'Requires an Opening on the opponent.',
      attack: {
        optimalRanges: ['Pocket', 'Clinch'], levels: ['Low'], power: 4, cost: 1, category: 'Grapple',
        text: 'Opt P/C • L • P4 • Cost 1. Requires an Opening on the opponent.'
      },
      defense: { levels: ['Mid', 'Low'], guard: 3, text: 'M/L • G3.' },
      v4: {
        technique: 'Pumpkin Roll', family: 'Grapple', speedPips: 1, speedLabel: 'Committed',
        continuation: { kind: 'Control', contactResult: 'setPocket', trigger: 'contact' },
        defenseFamily: 'Absorb',
        defenseResult: { contact: true, recover: 1, condition: 'noStaminaLostFromIncomingAttack' },
        attackBadge: 'GRAPPLE - CONTROL on Contact: set End at Pocket.',
        defenseBadge: 'ABSORB - Contact; Recover 1 if the incoming Attack caused no Stamina loss.',
        atkSummary: 'Range P/C | Level L | Power P4 | Grapple',
        defSummary: 'Level M/L | Guard 3'
      }
    },

    // =============================================================================================
    // WINTER FIGHT CAMP — "Cold air. Short breath. Clean technique."
    // =============================================================================================

    // ---------------------------------------------------------------------------------------------
    // CRP-WF-01 — Black-Ice Step
    //
    // Mind the Gap's Off-Balance clause on a Kick rather than a Grapple, and at Power 2 rather than
    // 3 — the cheaper, faster way to buy the same broken stance. Same translation, same reasoning.
    // ---------------------------------------------------------------------------------------------
    wfc_black_ice_step: {
      id: 'wfc_black_ice_step', name: 'Black-Ice Step', style: 'Universal', tempo: 22,
      ownershipTier: 'Challenge', ownershipGroup: 'ChallengeOnly',
      seasonal: SEASON, displayId: 'CRP-WF-01',
      traits: ['Kick', 'LowLine', 'Footwork'],
      moveGloss: 'Skate off the line and cut the supporting leg before the stance settles.',
      attackBeat: 'skates off the line and cuts the supporting leg out from under the stance',
      defenseBeat: 'glides clear before the line can catch them',
      defenseMethod: 'Check',
      attack: {
        optimalRanges: ['Long', 'Pocket'], levels: ['Low'], power: 2, cost: 0, category: 'Kick',
        onResolved: (ctx, result) => {
          if (result.cleanHitEffectsAllowed) ctx.setOffBalance(ctx.opp);
        },
        text: 'Opt L/P • L • P2. On a clean hit, the opponent is Off-Balance.'
      },
      defense: { levels: ['Mid', 'Low'], guard: 3, text: 'M/L • G3.' },
      v4: {
        technique: 'Black-Ice Step', family: 'Kick', speedPips: 3, speedLabel: 'Fast',
        continuation: { kind: 'ImpactRecovery', impactOnAbsorb: 1, recoveryOpeningLevel: 'Low',
                        recoveryOpeningType: 'Recovery', recoveryOpeningBonus: 1,
                        recoveryTrigger: ['Deflect', 'Dodge'] },
        defenseFamily: 'Dodge',
        defenseResult: { contact: false, attackBecomesWhiff: true,
                         end: { kind: 'shift', direction: 'farther', bands: 1 } },
        attackBadge: 'KICK - IMPACT 1 through Absorb; RECOVERY -> LOW after Deflect or Dodge.',
        defenseBadge: 'DODGE - Attack Whiffs; move End Range 1 band farther.',
        atkSummary: 'Range L/P | Level L | Power P2 | Kick',
        defSummary: 'Level M/L | Guard 3'
      }
    },

    // ---------------------------------------------------------------------------------------------
    // CRP-WF-02 — Snowblind Feint
    //
    // Designer, on the DEFENSE: shiftFlow, aftermath, self, amount -1, condition `fullGuard`.
    // Two traps in one line, both already sprung by sfs_split_gate_entry and recorded there:
    // `ctx.opp` is this card's OWN fighter inside a Defense hook, and the designer's -1 is
    // `ctx.shiftFlow(x, 1)` because that helper subtracts (see the sign note in this file's header).
    // ---------------------------------------------------------------------------------------------
    wfc_snowblind_feint: {
      id: 'wfc_snowblind_feint', name: 'Snowblind Feint', style: 'Universal', tempo: 20,
      ownershipTier: 'Challenge', ownershipGroup: 'ChallengeOnly',
      seasonal: SEASON, displayId: 'CRP-WF-02',
      traits: ['Punch', 'Feint', 'Combination'],
      moveGloss: 'Flash the lead hand across the eyes, then send the straight behind it.',
      attackBeat: 'flashes the lead hand across the eyes and sends the straight in behind it',
      defenseBeat: 'reads through the glare, turns the punch aside, and settles',
      defenseMethod: 'Deflect',
      attack: {
        optimalRanges: ['Long', 'Pocket'], levels: ['High'], power: 2, cost: 0, category: 'Punch',
        text: 'Opt L/P • H • P2.'
      },
      defense: {
        levels: ['High', 'Mid'], guard: 3,
        onResolved: (ctx, result) => { if (result.fullyStopped) ctx.shiftFlow(ctx.opp, 1); },
        text: 'H/M • G3. On a full guard, shift Flow -1.'
      },
      v4: {
        technique: 'Snowblind Feint', family: 'Punch', speedPips: 3, speedLabel: 'Fast',
        continuation: { kind: 'Chain', openingLevel: 'Mid', openingType: 'Combination', bonus: 1,
                        usableBy: ['Punch'], available: 'nextExchangeOnly', trigger: 'contact' },
        defenseFamily: 'Deflect',
        defenseResult: { contact: false, cancelContinuation: true, openingOn: 'attacker',
                         openingLevel: 'attemptedLevelUnlessRecoveryPrinted', bonus: 1,
                         againstGrappleControl: 'holdClash' },
        attackBadge: 'PUNCH - CHAIN -> MID: Contact creates the printed Punch-only Opening for next exchange.',
        defenseBadge: 'DEFLECT - Cancel continuation; create an Opening on the attacker at the resolved exposure level.',
        atkSummary: 'Range L/P | Level H | Power P2 | Punch',
        defSummary: 'Level H/M | Guard 3'
      }
    },

    // ---------------------------------------------------------------------------------------------
    // CRP-WF-03 — Frozen Clinch
    //
    // Designer: shiftFlow, aftermath, OPPONENT, amount +1, condition `attackDealsDamage`. A POSITIVE
    // amount on the shared track is a step toward Fury, so this is `ctx.shiftFury`, not
    // `ctx.shiftFlow` — the one card in this pack where the two helpers are not interchangeable, and
    // the reason the header spells the convention out. Pushing the victim toward Fury is the
    // attrition read: the clinch makes them fight hotter and worse.
    // ---------------------------------------------------------------------------------------------
    wfc_frozen_clinch: {
      id: 'wfc_frozen_clinch', name: 'Frozen Clinch', style: 'Universal', tempo: 34,
      ownershipTier: 'Challenge', ownershipGroup: 'ChallengeOnly',
      seasonal: SEASON, displayId: 'CRP-WF-03',
      traits: ['Grapple', 'Clinch', 'Attrition'],
      moveGloss: 'Lock chest-to-chest and make every breath feel colder and shorter.',
      attackBeat: 'locks chest-to-chest and makes every breath come back shorter',
      defenseBeat: 'hunkers down, takes the contact, and keeps the heat in',
      defenseMethod: 'Shell',
      attack: {
        optimalRanges: ['Pocket', 'Clinch'], levels: ['Mid'], power: 2, cost: 0, category: 'Grapple',
        // Designer: shiftFlow, aftermath, opponent, amount +1, condition `attackDealsDamage`.
        onResolved: (ctx, result) => { if (result.damage > 0) ctx.shiftFury(ctx.opp, 1); },
        text: 'Opt P/C • M • P2. On damage, the opponent shifts +1 toward Fury.'
      },
      defense: { levels: ['High', 'Mid'], guard: 3, text: 'H/M • G3.' },
      v4: {
        technique: 'Frozen Clinch', family: 'Grapple', speedPips: 2, speedLabel: 'Standard',
        continuation: { kind: 'Control', contactResult: 'holdClash', trigger: 'contact' },
        defenseFamily: 'Absorb',
        defenseResult: { contact: true, recover: 1, condition: 'noStaminaLostFromIncomingAttack' },
        attackBadge: 'GRAPPLE - CONTROL on Contact: hold End at Clash.',
        defenseBadge: 'ABSORB - Contact; Recover 1 if the incoming Attack caused no Stamina loss.',
        atkSummary: 'Range P/C | Level M | Power P2 | Grapple',
        defSummary: 'Level H/M | Guard 3'
      }
    },

    // =============================================================================================
    // CARNIVAL AFTER DARK — "Three attractions. Every one fights back."
    // =============================================================================================

    // ---------------------------------------------------------------------------------------------
    // CRP-CA-01 — Ring the Bell
    //
    // Designer: createOpening, aftermath, on the OPPONENT, { General, Mid, +1, anyAttack,
    // nextExchangeOnly }, condition `cleanHit`. `ctx.grantOpening(key, level, bonus)` defaults its
    // type to 'General' and an Opening in this engine is already next-exchange-only, so the whole
    // specification is the one call below.
    //
    // REVISED DURING THE PACKAGE'S OWN QA, per its VALIDATION_REPORT: it originally created a second
    // Opening as well, and the overlapping path was removed so the card buys exactly one follow-up.
    // The shipped JSON is what is transcribed here, not the CARD_REFERENCE prose.
    // ---------------------------------------------------------------------------------------------
    cad_ring_the_bell: {
      id: 'cad_ring_the_bell', name: 'Ring the Bell', style: 'Universal', tempo: 48,
      ownershipTier: 'Challenge', ownershipGroup: 'ChallengeOnly',
      seasonal: SEASON, displayId: 'CRP-CA-01',
      traits: ['Punch', 'Power', 'Committed'],
      moveGloss: 'Plant hard and drive the high line like the hammer at a strength game.',
      attackBeat: 'plants hard and drives the high line like a hammer at the strength game',
      defenseBeat: 'shells up and lets the heavy swing spend itself',
      defenseMethod: 'Shell',
      attack: {
        optimalRanges: ['Pocket'], levels: ['High'], power: 4, cost: 1, category: 'Punch',
        onResolved: (ctx, result) => {
          if (result.cleanHitEffectsAllowed) ctx.grantOpening(ctx.opp, 'Mid', 1);
        },
        text: 'Opt P • H • P4 • Cost 1. On a clean hit, create a Mid Opening on the opponent.'
      },
      defense: { levels: ['High', 'Mid'], guard: 3, text: 'H/M • G3.' },
      v4: {
        technique: 'Ring the Bell', family: 'Punch', speedPips: 1, speedLabel: 'Committed',
        // No continuation in the package: the conditional Opening IS this card's payoff, so it is
        // 'Authored' rather than a Chain that would grant a second one. Same shape the two summer
        // prop cards use for a card whose whole effect is in its printed text.
        continuation: { kind: 'Authored', summary: 'Conditional Mid Opening', trigger: 'cardText' },
        defenseFamily: 'Absorb',
        defenseResult: { contact: true, recover: 1, condition: 'noStaminaLostFromIncomingAttack' },
        attackBadge: 'RING THE BELL - AUTHORED: Conditional Mid Opening.',
        defenseBadge: 'ABSORB - Contact; Recover 1 if the incoming Attack caused no Stamina loss.',
        atkSummary: 'Range P | Level H | Power P4 | Punch',
        defSummary: 'Level H/M | Guard 3'
      }
    },

    // ---------------------------------------------------------------------------------------------
    // CRP-CA-02 — High-Wire Escape
    //
    // Corn Maze Cutback's Evade clause aimed High instead of Mid, with the Recovery Opening moved to
    // match. Same translation.
    // ---------------------------------------------------------------------------------------------
    cad_high_wire_escape: {
      id: 'cad_high_wire_escape', name: 'High-Wire Escape', style: 'Universal', tempo: 22,
      ownershipTier: 'Challenge', ownershipGroup: 'ChallengeOnly',
      seasonal: SEASON, displayId: 'CRP-CA-02',
      traits: ['Kick', 'Footwork', 'Acrobatic'],
      moveGloss: 'Lean beyond the line and hook the heel back from an impossible angle.',
      attackBeat: 'leans out beyond reach and hooks the heel back across the high line',
      defenseBeat: 'sways off the wire and lets the attack pass beneath',
      defenseMethod: 'Weave',
      attack: {
        optimalRanges: ['Long', 'Pocket'], levels: ['High'], power: 2, cost: 0, category: 'Kick',
        powerMod: (ctx) => (ctx.attChoice.positionIntent === 'Evade' && ctx.clashFarther) ? 1 : 0,
        text: 'Opt L/P • H • P2. If you chose Evade and Clash is farther than Start, +1 Power.'
      },
      defense: { levels: ['High', 'Low'], guard: 3, text: 'H/L • G3.' },
      v4: {
        technique: 'High-Wire Escape', family: 'Kick', speedPips: 3, speedLabel: 'Fast',
        continuation: { kind: 'ImpactRecovery', impactOnAbsorb: 1, recoveryOpeningLevel: 'High',
                        recoveryOpeningType: 'Recovery', recoveryOpeningBonus: 1,
                        recoveryTrigger: ['Deflect', 'Dodge'] },
        defenseFamily: 'Dodge',
        defenseResult: { contact: false, attackBecomesWhiff: true,
                         end: { kind: 'shift', direction: 'farther', bands: 1 } },
        attackBadge: 'KICK - IMPACT 1 through Absorb; RECOVERY -> HIGH after Deflect or Dodge.',
        defenseBadge: 'DODGE - Attack Whiffs; move End Range 1 band farther.',
        atkSummary: 'Range L/P | Level H | Power P2 | Kick',
        defSummary: 'Level H/L | Guard 3'
      }
    },

    // ---------------------------------------------------------------------------------------------
    // CRP-CA-03 — Mirror Step
    //
    // Designer: ignoreGuard 1, beforeGuard, condition `opponentDefenseCoverageCountAtLeast` count 2.
    // Transcribed from sfs_two_on_one_turn, which carries the identical clause and whose hook reads
    // `ctx.defCard.defense.levels` — including the `'All'` spelling, because a defense that covers
    // everything covers at least two things and a length check on a string would silently miss it.
    // ---------------------------------------------------------------------------------------------
    cad_mirror_step: {
      id: 'cad_mirror_step', name: 'Mirror Step', style: 'Universal', tempo: 22,
      ownershipTier: 'Challenge', ownershipGroup: 'ChallengeOnly',
      seasonal: SEASON, displayId: 'CRP-CA-03',
      traits: ['Punch', 'Read', 'Entry', 'Combination'],
      moveGloss: "Reflect the guard's shape, then step through the seam it reveals.",
      attackBeat: 'matches the guard for a beat and steps straight through the seam it opens',
      defenseBeat: 'mirrors the angle, turns it aside, and settles',
      defenseMethod: 'Deflect',
      attack: {
        optimalRanges: ['Pocket', 'Clinch'], levels: ['Mid'], power: 2, cost: 0, category: 'Punch',
        guardIgnore: (ctx) => {
          const lv = ctx.defCard.defense.levels;
          return (lv === 'All' || (Array.isArray(lv) && lv.length >= 2)) ? 1 : 0;
        },
        text: 'Opt P/C • M • P2. If their Defense covers 2 or more levels, ignore 1 Guard.'
      },
      defense: {
        levels: ['High', 'Mid'], guard: 3,
        onResolved: (ctx, result) => { if (result.fullyStopped) ctx.shiftFlow(ctx.opp, 1); },
        text: 'H/M • G3. On a full guard, shift Flow -1.'
      },
      v4: {
        technique: 'Mirror Step', family: 'Punch', speedPips: 3, speedLabel: 'Fast',
        continuation: { kind: 'Chain', openingLevel: 'High', openingType: 'Combination', bonus: 1,
                        usableBy: ['Punch'], available: 'nextExchangeOnly', trigger: 'contact' },
        defenseFamily: 'Deflect',
        defenseResult: { contact: false, cancelContinuation: true, openingOn: 'attacker',
                         openingLevel: 'attemptedLevelUnlessRecoveryPrinted', bonus: 1,
                         againstGrappleControl: 'holdClash' },
        attackBadge: 'PUNCH - CHAIN -> HIGH: Contact creates the printed Punch-only Opening for next exchange.',
        defenseBadge: 'DEFLECT - Cancel continuation; create an Opening on the attacker at the resolved exposure level.',
        atkSummary: 'Range P/C | Level M | Power P2 | Punch',
        defSummary: 'Level H/M | Guard 3'
      }
    }
  });
})();
